import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Chip from '@mui/material/Chip'
import PageHeader from '../../common/PageHeader'
import Section, { SubSection } from '../../common/Section'
import Alert from '../../common/Alert'
import CodeBlock, { SimpleCodeBlock, InlineCode } from '../../common/CodeBlock'
import { ParamTable } from '../../common/Table'
import Table from '../../common/Table'
import EndpointBadge from '../../common/EndpointBadge'
import { useLocale } from '../../../i18n/useLocale'

export default function ParcelTracking() {
  const { urls, examples, brand } = useLocale()

  return (
    <Box>
      <PageHeader
        title="Parcel Tracking"
        description="Query real-time parcel status using barcode or partner reference."
        breadcrumbs={[
          { label: 'Guides', path: '/guides' },
          { label: 'Parcel Tracking' },
        ]}
      />

      <Section title="Overview" divider={false}>
        <Typography variant="body1" sx={{ mb: 2 }}>
          The Status API allows you to track parcels using either the {brand.name} parcel number
          or your own partner parcel number. Both single and batch queries are supported.
        </Typography>
        <Alert type="info">
          Status queries are cached for performance. Single parcel queries are cached; batch queries are not.
        </Alert>
      </Section>

      <Section title="Single Parcel Status">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <SimpleCodeBlock code="GET /parcels/status?barcode={parcelNumber}" />
          <EndpointBadge type="public" />
        </Box>

        <SubSection title="Query Parameters">
          <ParamTable
            params={[
              {
                name: 'barcode',
                type: 'string',
                required: true,
                description: `The parcel number (${brand.name} or partner parcel number)`,
              },
            ]}
          />
        </SubSection>

        <SubSection title="Example Request">
          <CodeBlock
            title="Get Parcel Status"
            examples={[
              {
                language: 'bash',
                label: 'cURL',
                code: `curl -X GET "${urls.apiBase}/parcels/status?barcode=Q17563175099981697" \\
  -H "Accept: application/json"`,
              },
              {
                language: 'javascript',
                label: 'Node.js',
                code: `const parcelNumber = 'Q17563175099981697';

const response = await fetch(
  \`${urls.apiBase}/parcels/status?barcode=\${parcelNumber}\`,
  { headers: { 'Accept': 'application/json' } }
);

const status = await response.json();
console.log('Current status:', status.statuses[0].statusName);`,
              },
              {
                language: 'python',
                label: 'Python',
                code: `import requests

parcel_number = 'Q17563175099981697'

response = requests.get(
    f'${urls.apiBase}/parcels/status',
    params={'barcode': parcel_number}
)

status = response.json()
print(f"Current status: {status['statuses'][0]['statusName']}")`,
              },
            ]}
          />
        </SubSection>

        <SubSection title="Response">
          <SimpleCodeBlock
            code={`{
  "parcelNumber": "Q17563175099981697",
  "partnerParcelNumber": "ORD-2024-001",
  "parcelType": "B2C",
  "statuses": [
    {
      "createdAt": "2026-01-15 16:45:00",
      "statusName": "COURIN",
      "statusDescription": "Parcel was dropped off by the courier.",
      "statusShortDescription": "At APM"
    },
    {
      "createdAt": "2026-01-15 12:30:00",
      "statusName": "SORTROUT",
      "statusDescription": "Parcel was sorted for the outbound route.",
      "statusShortDescription": "In Transit"
    },
    {
      "createdAt": "2026-01-15 08:00:00",
      "statusName": "INSYS",
      "statusDescription": "Parcel was created in the system.",
      "statusShortDescription": "Created"
    }
  ],
  "destinationApm": {
    "name": "${examples.apmName}",
    "formattedAddress": "${examples.apmLocation}"
  }
}`}
          />
        </SubSection>
      </Section>

      <Section title="Batch Status Query">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Typography variant="body1">
            Query multiple parcels in a single request (up to 1000 parcels):
          </Typography>
          <EndpointBadge type="apikey" />
        </Box>

        <SimpleCodeBlock code="POST /parcels/status" />

        <SubSection title="Request Body">
          <Typography variant="body2" sx={{ mb: 2 }}>
            Send an array of parcel numbers (barcodes):
          </Typography>
          <SimpleCodeBlock
            code={`["Q17563175099981697", "Q17564837305001818", "ORD-2024-003"]`}
          />
        </SubSection>

        <SubSection title="Example Request">
          <CodeBlock
            title="Batch Status Query"
            examples={[
              {
                language: 'bash',
                label: 'cURL',
                code: `curl -X POST "${urls.apiBase}/parcels/status" \\
  -H "Content-Type: application/json" \\
  -H "X-API-KEY: your-api-key" \\
  -d '["Q17563175099981697", "Q17564837305001818", "ORD-2024-003"]'`,
              },
              {
                language: 'javascript',
                label: 'Node.js',
                code: `const barcodes = [
  'Q17563175099981697',
  'Q17564837305001818',
  'ORD-2024-003'  // Can also use partner parcel number
];

const response = await fetch('${urls.apiBase}/parcels/status', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'X-API-KEY': process.env.API_KEY,
  },
  body: JSON.stringify(barcodes)
});

const statuses = await response.json();
statuses.forEach(parcel => {
  console.log(\`\${parcel.parcelNumber}: \${parcel.statuses[0].statusName}\`);
});`,
              },
            ]}
          />
        </SubSection>

        <SubSection title="Response">
          <SimpleCodeBlock
            code={`[
  {
    "parcelNumber": "Q17563175099981697",
    "partnerParcelNumber": "ORD-2024-001",
    "parcelType": "B2C",
    "statuses": [...],
    "destinationApm": {...}
  },
  {
    "parcelNumber": "Q17564837305001818",
    "partnerParcelNumber": "ORD-2024-002",
    "parcelType": "B2C",
    "statuses": [...],
    "destinationApm": {...}
  }
]`}
          />
        </SubSection>

        <Alert type="warning" title="Batch Limits & Semantics">
          Maximum 1000 barcodes per request; empty arrays and blank barcodes are rejected (400).
          <strong> Unknown barcodes are silently omitted from the response</strong> — no error, no
          placeholder — and result order is not guaranteed to match request order. Match results
          back to your request by <InlineCode>parcelNumber</InlineCode> /{' '}
          <InlineCode>partnerParcelNumber</InlineCode> and treat missing entries as "not found".
        </Alert>
      </Section>

      <Section title="Parcel Status Codes">
        <Typography variant="body1" sx={{ mb: 2 }}>
          Parcels progress through the following status states. Statuses are listed in typical flow order.
        </Typography>

        <SubSection title="Sender Drop-off">
          <Table
            columns={[
              { key: 'status', header: 'Status', width: '25%' },
              { key: 'description', header: 'Description' },
            ]}
            data={[
              {
                status: <Chip label="INSYS" size="small" sx={{ bgcolor: '#E3F2FD' }} />,
                description: 'The parcel was created in the system by the sender.',
              },
              {
                status: <Chip label="SINGLEIN" size="small" sx={{ bgcolor: '#E3F2FD' }} />,
                description: 'The parcel was dropped off by the sender (slot door closed).',
              },
              {
                status: <Chip label="BATCHIN" size="small" sx={{ bgcolor: '#E3F2FD' }} />,
                description: 'APM event: In multi-parcel-sending mode, a parcel was dropped off by the sender.',
              },
              {
                status: <Chip label="C2BIN" size="small" sx={{ bgcolor: '#E3F2FD' }} />,
                description: 'The return parcel was dropped off by the original recipient.',
              },
            ]}
          />
        </SubSection>

        <SubSection title="Collection & Transit">
          <Table
            columns={[
              { key: 'status', header: 'Status', width: '25%' },
              { key: 'description', header: 'Description' },
            ]}
            data={[
              {
                status: <Chip label="COUROUT" size="small" sx={{ bgcolor: '#FFF3E0' }} />,
                description: 'Parcel was picked up by the courier (taken out of the APM / collected from the sender).',
              },
              {
                status: <Chip label="INWAREHOUSE" size="small" sx={{ bgcolor: '#FFF3E0' }} />,
                description: 'Parcel arrived at the warehouse.',
              },
              {
                status: <Chip label="SORTROUT" size="small" sx={{ bgcolor: '#FFF3E0' }} />,
                description: 'Parcel was sorted at the warehouse to its outbound route.',
              },
              {
                status: <Chip label="SORTAPM" size="small" sx={{ bgcolor: '#FFF3E0' }} />,
                description: 'Parcel was sorted for the APM route.',
              },
            ]}
          />
        </SubSection>

        <SubSection title="Delivery">
          <Table
            columns={[
              { key: 'status', header: 'Status', width: '25%' },
              { key: 'description', header: 'Description' },
            ]}
            data={[
              {
                status: <Chip label="COURIN" size="small" sx={{ bgcolor: '#C8E6C9' }} />,
                description: 'Parcel was dropped off at the destination APM by the courier. This is the moment the recipient receives the collection code.',
              },
              {
                status: <Chip label="RECEIVED" size="small" color="success" />,
                description: 'The parcel was taken out of the locker by the recipient.',
              },
              {
                status: <Chip label="COLLECTED" size="small" color="success" />,
                description: 'Parcel was collected by the receiver.',
              },
            ]}
          />
        </SubSection>

        <SubSection title="APM Events">
          <Table
            columns={[
              { key: 'status', header: 'Status', width: '25%' },
              { key: 'description', header: 'Description' },
            ]}
            data={[
              {
                status: <Chip label="SLOTCHANGE" size="small" sx={{ bgcolor: '#F3E5F5' }} />,
                description: 'APM event: The courier changed the box/slot of a parcel in the same APM.',
              },
            ]}
          />
        </SubSection>

        <SubSection title="Returns & Exceptions">
          <Table
            columns={[
              { key: 'status', header: 'Status', width: '25%' },
              { key: 'description', header: 'Description' },
            ]}
            data={[
              {
                status: <Chip label="C2BIN" size="small" sx={{ bgcolor: '#FFCDD2' }} />,
                description: 'A return parcel was dropped off by the original recipient.',
              },
              {
                status: <Chip label="COLLECTSENT" size="small" sx={{ bgcolor: '#FFCDD2' }} />,
                description: 'Return parcel was sent out from the warehouse.',
              },
              {
                status: <Chip label="RETURNED" size="small" sx={{ bgcolor: '#FFCDD2' }} />,
                description: 'Parcel was returned to the sender.',
              },
              {
                status: <Chip label="LOST" size="small" sx={{ bgcolor: '#FFCDD2' }} />,
                description: 'Parcel location is unknown.',
              },
              {
                status: <Chip label="DESTROYED" size="small" sx={{ bgcolor: '#FFCDD2' }} />,
                description: 'Parcel has been destroyed and no longer exists.',
              },
            ]}
          />
        </SubSection>

        <Alert type="info" title="Technical Statuses">
          Status definitions are stored server-side and the set may grow over time — treat unknown
          status codes as opaque strings. Internal/technical statuses (e.g.{' '}
          <InlineCode>DETOUR</InlineCode>, <InlineCode>HOLDOFF</InlineCode>) are filtered out of the
          tracking API responses; note that the <strong>status webhook below is not filtered</strong>{' '}
          and may deliver them.
        </Alert>
      </Section>

      <Section title="Status Webhooks (Push Callbacks)">
        <Typography variant="body1" sx={{ mb: 2 }}>
          Instead of polling, register a callback URL and receive a POST for every status saved on
          your parcels. Configure the callback URL, username, and password in the{' '}
          <strong>Customer Portal → Settings</strong> (a logged-in account administrator can set or
          change them at any time — there is no API endpoint for this).
        </Typography>
        <SubSection title="Delivery Contract">
          <Box component="ul" sx={{ pl: 3, '& li': { mb: 1 } }}>
            <li>
              Fires on <strong>every</strong> status saved for your parcels — including technical
              statuses the tracking API filters out — after the status is committed.
            </li>
            <li>
              <InlineCode>POST</InlineCode> to your URL with{' '}
              <InlineCode>Content-Type: application/json</InlineCode> and <strong>HTTP Basic auth</strong>{' '}
              using your registered credentials. The callback only fires when URL, username{' '}
              <em>and</em> password are all configured.
            </li>
            <li>Respond with any 2xx status.</li>
            <li>
              <strong>Single-attempt delivery — there are no retries.</strong> Treat the webhook as a
              latency optimization and reconcile against the tracking endpoints; do not rely on it as
              the sole source of truth.
            </li>
          </Box>
        </SubSection>
        <SubSection title="Payload">
          <Typography variant="body2" sx={{ mb: 1 }}>
            Note the field name <InlineCode>statusId</InlineCode> — it carries the status{' '}
            <em>code name</em> as a string, not a numeric ID:
          </Typography>
          <SimpleCodeBlock
            code={`{
  "parcelId": 123456,
  "statusId": "COURIN",
  "parcelNumber": "Q17563175099981697"
}`}
          />
        </SubSection>
      </Section>

      <Section title="Response Structure">
        <SubSection title="ParcelStatusResponse">
          <ParamTable
            params={[
              { name: 'parcelNumber', type: 'string', required: true, description: `${brand.name} parcel tracking number` },
              { name: 'partnerParcelNumber', type: 'string', required: false, description: 'Your reference number (if provided during creation)' },
              { name: 'parcelType', type: 'string', required: true, description: 'Parcel type: C2C, B2C, MK2C, or BTI' },
              { name: 'statuses', type: 'array', required: true, description: 'Array of status history (newest first)' },
              { name: 'destinationApm', type: 'object', required: true, description: 'Destination APM details' },
            ]}
          />
        </SubSection>

        <SubSection title="StatusDto">
          <ParamTable
            params={[
              { name: 'createdAt', type: 'string', required: true, description: 'Timestamp of status change (yyyy-MM-dd HH:mm:ss)' },
              { name: 'statusName', type: 'string', required: true, description: 'Status code (e.g., RECEIVED, IN_TRANSIT)' },
              { name: 'statusDescription', type: 'string', required: true, description: 'Full description of the status' },
              { name: 'statusShortDescription', type: 'string', required: true, description: 'Short description for display' },
            ]}
          />
        </SubSection>

        <SubSection title="ApmDto">
          <ParamTable
            params={[
              { name: 'name', type: 'string', required: true, description: 'APM location name' },
              { name: 'formattedAddress', type: 'string', required: true, description: 'Full address (addressLine, city)' },
            ]}
          />
        </SubSection>
      </Section>

      <Section title="Error Handling">
        <SimpleCodeBlock
          code={`{
  "timestamp": "2024-01-15 14:30:00",
  "message": "Parcel not found!",
  "status": 404
}`}
        />

        <Typography variant="body1" sx={{ mt: 2 }}>
          Common errors:
        </Typography>
        <Box component="ul" sx={{ pl: 3, mt: 1 }}>
          <li><InlineCode>400</InlineCode> - Invalid barcode, empty request, too many barcodes, <strong>or parcel not found</strong> — the platform returns 400 (not 404) with <InlineCode>errorCode: PARCEL_NOT_FOUND</InlineCode> for unknown single-lookup barcodes</li>
          <li><InlineCode>401</InlineCode> - Missing/invalid API key (batch endpoint)</li>
        </Box>
      </Section>
    </Box>
  )
}
